import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { API_URL } from '../../../modules/auth/core/_requests';
import Sparkline from './Sparkline';

// Helper function to shorten and clean product names
const formatProductName = (name: string, brand: string) => {
    let formattedName = name.replace(new RegExp(brand, 'ig'), '').trim();
    if (formattedName.length > 35) {
        formattedName = `${formattedName.substring(0, 35)}...`;
    }
    return formattedName || name; // Fallback to original name if empty
};

const AsinLink: React.FC<{ asin: string }> = ({ asin }) => (
    <a href={`https://amazon.com/dp/${asin}`} target="_blank" rel="noopener noreferrer" className="text-white fw-bold text-hover-primary">
        {asin}
    </a>
);


// Types
interface SalesData {
    Product: string;
    ProductName: string;
    Brand: string;
    Total: number;
    'Jan-24': number; 'Jan-24_Percent': number;
    'Feb-24': number; 'Feb-24_Percent': number;
    'Mar-24': number; 'Mar-24_Percent': number;
    'Apr-24': number; 'Apr-24_Percent': number;
    'May-24': number; 'May-24_Percent': number;
    'Jun-24': number; 'Jun-24_Percent': number;
    'Jul-24': number; 'Jul-24_Percent': number;
    'Aug-24': number; 'Aug-24_Percent': number;
    'Sep-24': number; 'Sep-24_Percent': number;
    'Oct-24': number; 'Oct-24_Percent': number;
    'Nov-24': number; 'Nov-24_Percent': number;
    'Dec-24': number; 'Dec-24_Percent': number;
    date_uploaded: string;
}

interface BrandTotal {
    Brand: string;
    totalUnits: number;
}

interface TopProduct {
    Product: string;
    ProductName:string;
    Brand: string;
    Total: number;
}

interface DashboardData {
    brandTotals: BrandTotal[];
    topProductsByBrand: { [key: string]: TopProduct[] };
}

// Main Component
export const AmazonHelium10UploaderPage: React.FC = () => {
    // State
    const [salesData, setSalesData] = useState<SalesData[]>([]);
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rows, setRows] = useState(10);
    const [globalFilter, setGlobalFilter] = useState('');
    const [hoveredPoint, setHoveredPoint] = useState<{ month: string | null; rowIndex: number | null }>({ month: null, rowIndex: null });
    const toast = useRef<Toast>(null);
    const [exporting, setExporting] = useState(false);

    const months = ['Jan-24', 'Feb-24', 'Mar-24', 'Apr-24', 'May-24', 'Jun-24', 'Jul-24', 'Aug-24', 'Sep-24', 'Oct-24', 'Nov-24', 'Dec-24'];

    // Data Fetching
    const fetchDashboardData = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/de-apps/helium10-market360/dashboard`);
            setDashboardData(data);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    }

    const fetchTableData = async (page = 1, searchTerm = '') => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/de-apps/helium10-market360/data`, {
                params: { page, search: searchTerm, limit: rows }
            });
            setSalesData(response.data.data);
            setTotalRecords(response.data.total);
        } catch (error) {
            console.error('Error fetching sales data:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch sales data.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        fetchTableData(currentPage, globalFilter);
    }, [currentPage, rows]);

    // Event Handlers
    const onPageChange = (event: PaginatorPageChangeEvent) => {
        setCurrentPage(event.page + 1);
        setRows(event.rows);
    };

    const handleSearch = () => {
        setCurrentPage(1);
        fetchTableData(1, globalFilter);
    };

    const handleUpload = async (event: FileUploadHandlerEvent) => {
        const file = event.files[0];
        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            const response = await axios.post(`${API_URL}/api/de-apps/helium10-market360/upload`, formData);
            toast.current?.show({ severity: 'success', summary: 'Success', detail: `${response.data.count} records uploaded successfully.` });
            fetchDashboardData();
            fetchTableData(1, '');
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'File upload failed.' });
            console.error('Upload error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await axios.get(`${API_URL}/api/de-apps/helium10-market360/export`);
            const dataToExport = response.data.map((row: any) => {
                const newRow: any = {
                    ASIN: row.Product,
                    'Product Name': row.ProductName,
                    Brand: row.Brand,
                    'Total Sales': row.Total
                };
                months.forEach(month => {
                    newRow[`${month} Units`] = row[month];
                    newRow[`${month} Percent`] = row[`${month}_Percent`];
                });
                return newRow;
            });

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const data = new Blob([excelBuffer], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"});
            saveAs(data, "Helium10_Sales_Export.xlsx");

        } catch (error) {
            console.error('Export error:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to export data.' });
        } finally {
            setExporting(false);
        }
    };


    // Table Config
    const tableHeader = (
        <div className="d-flex justify-content-between align-items-center p-4" >
            <h5 className="m-0 text-white">Full Sales Data</h5>
            <div >
                <span className="p-input-icon-left me-2">
                    <i className="pi pi-search" />
                    <InputText type="search" value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search ASIN or Name" onKeyDown={(e) => e.key === 'Enter' && handleSearch()} className="p-inputtext-sm" />
                </span>
                <Button label="Export to Excel" icon="pi pi-file-excel" className="p-button-success p-button-sm" onClick={handleExport} loading={exporting} />
            </div>
        </div>
    );
    
    const monthCellBody = (rowData: SalesData, month: string) => (
        <div className="text-center">
            <div className="fw-bold">{(rowData[month as keyof SalesData] as number)?.toLocaleString() || 0}</div>
            <div className="text-muted" style={{ fontSize: '0.8em', color: '#a1a5b7' }}>{Number(rowData[`${month}_Percent` as keyof SalesData])?.toFixed(2) || '0.00'}%</div>
        </div>
    );
    
    const tableColumns = [
        { field: "Product", header: "ASIN", body: (rowData: SalesData) => <AsinLink asin={rowData.Product} /> },
        { field: "ProductName", header: "Product Name" },
        { field: "Brand", header: "Brand" },
        { field: "Total", header: "Total" },
        {
            field: 'trend',
            header: 'Annual Trend',
            body: (rowData: SalesData, { rowIndex }: any) => {
                const trendData = months.map(month => (rowData as any)[month] || 0);
                return <Sparkline data={trendData} labels={months} hoveredMonth={hoveredPoint.rowIndex === rowIndex ? hoveredPoint.month : null} onHover={(month) => setHoveredPoint({ month, rowIndex })} />;
            }
        },
        ...months.map(month => ({
            field: month,
            header: month,
            body: (rowData: SalesData, { rowIndex }: any) => (
                <div
                    onMouseEnter={() => setHoveredPoint({ month, rowIndex })}
                    onMouseLeave={() => setHoveredPoint({ month: null, rowIndex: null })}
                >
                    {monthCellBody(rowData, month)}
                </div>
            )
        }))
    ];

    // Render JSX
    return (
        <div className="p-4" style={{ backgroundColor: '#151521' }}>
            <Toast ref={toast} />

            <div className="card mb-4" style={{ backgroundColor: '#1e1e2d' }}>
                <div className="card-body" >
                    <h2 className="card-title text-white mb-2">Helium 10 Sales Dashboard</h2>
                    <p className="card-text text-muted">Upload your sales export to visualize and analyze brand and product performance.</p>
                    <FileUpload
                        name="file" customUpload uploadHandler={handleUpload} accept=".xlsx,.csv" maxFileSize={10000000}
                        className="p-button-sm" chooseLabel="Select File" uploadLabel="Upload" cancelLabel="Cancel" disabled={loading}
                    />
                </div>
            </div>

            {dashboardData && (
                <div className="mb-4">
                    <h3 className="text-white mb-3">Top 5 Brand Performance</h3>
                    <div className="row g-3">
                        {dashboardData.brandTotals.slice(0, 5).map(brand => (
                            <div key={brand.Brand} className="col">
                                <div className="card h-100" style={{ backgroundColor: '#1e1e2d' }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center mb-2">
                                            <h4 className="text-primary me-2 mb-0">{brand.Brand}</h4>
                                            <span className="badge" style={{backgroundColor: '#0d6efd', color: '#fff'}}>{brand.totalUnits.toLocaleString()} units</span>
                                        </div>
                                        <ul className="list-group list-group-flush">
                                            {dashboardData.topProductsByBrand[brand.Brand]?.map(p => (
                                                <li key={p.Product} className="list-group-item d-flex justify-content-between align-items-center p-2" style={{ backgroundColor: 'transparent', borderColor: 'rgba(255,255,255,0.1)' }}>
                                                    <div className="text-muted" style={{fontSize: '0.8rem'}}>
                                                        <AsinLink asin={p.Product} />
                                                        <small className="d-block" title={p.ProductName}>{formatProductName(p.ProductName, p.Brand)}</small>
                                                    </div>
                                                    <span className="badge" style={{backgroundColor: '#0d6efd', color: '#fff', fontSize: '0.75rem'}}>{p.Total.toLocaleString()}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            <div className="card" style={{ backgroundColor: '#1e1e2d' }}>
                <DataTable value={salesData} header={tableHeader} loading={loading} scrollable scrollHeight="800px" className="p-datatable-dark" rowHover>
                    {tableColumns.map(col => {
                        const style: React.CSSProperties = { minWidth: col.field === 'ProductName' ? '250px' : '150px' };
                        if (months.includes(col.field)) {
                            style.width = '100px';
                            style.minWidth = '100px';
                        }

                        return (
                            <Column
                                key={col.field}
                                field={col.field}
                                header={col.header}
                                body={col.body}
                                sortable
                                style={style}
                            />
                        )
                    })}
                </DataTable>
                <Paginator first={(currentPage - 1) * rows} rows={rows} totalRecords={totalRecords} onPageChange={onPageChange} rowsPerPageOptions={[10, 20, 50, 100]} className="p-4 border-top border-secondary" />
            </div>
             <style>{`
                .p-datatable-dark .p-datatable-thead > tr > th { background: #2a2a3f; }
            `}</style>
        </div>
    );
};
